import type {Address, BookingDataProps, DeskPricesDTO, FinalizeProductDTO, FinalizeResponse, Goal, IAdminEditBookingDTO, IBookingReasonDTO, ICalendarStateData, ICreateCompanyLeadDTO, IGeneralBFLead, IGetPaymentInfo, IModelsInput, IMoveBookingDTO, IPaymentPrice, IPreviousBooking, IPrivateBookingPaymentsDTO, IThirdPartyGiftCardType, ModifyBookPartnerDetails, PackageTemplate, Partner, Partners, RRDate, Service} from "@raskrask-dk/raskrask-utils";
import {ModelFakeFactory} from ".";
import type {IRepository} from "..";
import { IGoalDTO, IServiceDTO, IPartnerDTO, IBookableSchedulesDTO, IBasisTypeOffered, IPartnerAdsBookingSaleSubmission, IPartnerAdsCompanyLeadSubmission } from "../interfaces";


export class RepositoryFake implements IRepository {
  private factory: ModelFakeFactory = new ModelFakeFactory();

  private _mockScheduleDTOs(): {goals: IGoalDTO[]; services: IServiceDTO[]; partners: IPartnerDTO[]} {
    const goals = this.factory.makeGoalDTOs(10);
    const services = this.factory.makeServiceDTOs(goals.length * 5);
    const partners = this.factory.makePartnerDTOs(50);
    return {
      goals,
      services,
      partners,
    };
  }

  getBookingsByPartnerIdAndPeriod(profileId: number, serviceFrom: RRDate, serviceTo: RRDate): Promise<BookingDataProps[]> {
    throw new Error("Method not implemented.");
  }

  getAllPartners(_geoLat?: string, _geoLng?: string): Promise<Partner[]> {
    throw new Error("Method not implemented.");
  }

  getAllServices(): Promise<Service[]> {
    throw new Error("Method not implemented.");
  }

  getSchedules(): Promise<IBookableSchedulesDTO> {
    const mocks = this._mockScheduleDTOs();
    const goals = mocks.goals;

    // services are added to goals
    // kept unique per goal
    const services = goals.reduce((services, goal, i) => {
      const servicesPerGoal = 5;
      const from = i * servicesPerGoal;
      const to = from + servicesPerGoal;
      const goalServices = mocks.services.slice(from, to);
      goalServices.forEach((service) => goal.serviceIds.push(service.id));
      return [...services, ...goalServices];
    }, [] as IServiceDTO[]);

    // random amount of partners per service
    // filters out duplicates
    const partners = services
      .reduce((partners, service) => {
        const servicePartners = this.factory.randomElements(mocks.partners);
        service.profileIds = servicePartners.map((partner) => partner.id);
        return [...partners, ...servicePartners];
      }, [] as IPartnerDTO[])
      .filter((partner, i, self) => self.findIndex((p) => p.id === partner.id) === i);

    return new Promise(() => {
      return {
        goals: goals,
        services: services,
        partners: partners,
      };
    });
  }

  getOfferedBasisTypes(address: Address, startDate: string, numberOfDaysToCheck: number): Promise<IBasisTypeOffered[]> {
    throw new Error("Method not implemented.");
  }
  getPricesByProfileId(filters: {profileId: number}): Promise<DeskPricesDTO[]> {
    throw new Error("Method not implemented.");
  }

  getNumberOfServiceReviews(): Promise<number> {
    const numberOfReviews = this.factory.number({min: 1000, max: 9999});
    return new Promise(() => numberOfReviews);
  }
  getBookingData(bookingId: string): Promise<IPreviousBooking> {
    throw new Error("Method not implemented.");
  }
  getThirdPartyGiftCardTypes(): Promise<IThirdPartyGiftCardType[]> {
    throw new Error("Method not implemented.");
  }
  getPaymentInfo(paymentInfo: IGetPaymentInfo): Promise<IPaymentPrice> {
    throw new Error("Method not implemented.");
  }
  createCompanyLead(lead: ICreateCompanyLeadDTO): Promise<{status: number}> {
    throw new Error("Method not implemented.");
  }
  createGeneralLead(lead: IGeneralBFLead): Promise<{status: number}> {
    throw new Error("Method not implemented.");
  }
  finalizeProduct(product: FinalizeProductDTO, accessToken?: string): Promise<FinalizeResponse> {
    throw new Error("Method not implemented.");
  }
  removeReepayPaymentMethodTokenIfAny(customerFirstName: string, customerEmail: string): Promise<{message: "Success"}> {
    throw new Error("Method not implemented.");
  }
  saveReepayPaymentMethodToken(customerFirstName: string, customerEmail: string, paymentMethodToken: string): Promise<{message: "Success"}> {
    throw new Error("Method not implemented.");
  }
  submitPartnerAdsSale(input: IPartnerAdsBookingSaleSubmission): Promise<string> {
    throw new Error("Method not implemented.");
  }
  submitPartnerAdsLead(input: IPartnerAdsCompanyLeadSubmission): Promise<string> {
    throw new Error("Method not implemented.");
  }
  getBookingReasons(): Promise<IBookingReasonDTO[]> {
    throw new Error("Method not implemented.");
  }
  moveBooking(moveBookingObj: IMoveBookingDTO): Promise<"Success"> {
    throw new Error("Method not implemented.");
  }
  adminEditBooking(moveBookingObj: IAdminEditBookingDTO, accessToken?: string): Promise<"Success"> {
    throw new Error("Method not implemented.");
  }
  createModels(bookingflowDTO: IModelsInput): Promise<void> {
    throw new Error("Method not implemented.");
  }
  fetchMoreModels(bookingflowDTO: IModelsInput): Promise<void> {
    throw new Error("Method not implemented.");
  }
  getDefaultPrice(service: Service): number {
    throw new Error("Method not implemented.");
  }

  get calendarData(): {[key: number]: ICalendarStateData} {
    throw new Error("Method not implemented.");
  }
  get partners(): Partners {
    throw new Error("Method not implemented.");
  }
  get goals(): Goal[] {
    throw new Error("Method not implemented.");
  }
  get services(): Service[] {
    throw new Error("Method not implemented.");
  }
  get packages(): PackageTemplate[] {
    throw new Error("Method not implemented.");
  }

  getLastKlippeCardCodeByPurchaserEmail(_email: string): Promise<string | undefined> {
    return new Promise(() => "RR1313131");
  }
  cancelBookingCustomer(_bookingId: string): Promise<"Success"> {
    return Promise.resolve("Success");
  }
  cancelBookingAdmin(
    _bookingId: string,
    _isCancellationFee: boolean,
    _cancellationFeeSize: number,
    _shouldSendPartnerMessages: boolean,
    _shouldSendCustomerMessages: boolean,
    _shouldCloseTime: boolean,
    _cancelOnBehalfOf: string,
    _cancelReason: string
  ): Promise<"Success"> {
    return Promise.resolve("Success");
  }
  getModifyBookPartnerDetails(bookingId: string): Promise<ModifyBookPartnerDetails> {
    throw new Error("Method not implemented.");
  }

  getInfoAboutPaidAmount(_bookingId: string): Promise<IPrivateBookingPaymentsDTO> {
    throw new Error("Method not implemented.");
  }
}
